// Component: PageHeader.jsx
// Purpose: DS page-identity header (the "Identity" slot from the design system's
//          page archetypes). One consistent treatment for every page's title +
//          description, with fixed top spacing so the heading/description land in
//          the SAME position on every page. Tokenized: title in Inter/800, the
//          optional emphasis word in the theme accent, lede in --fg-2.
//
// Usage:
//   <PageHeader title="Market" accent="Watch"
//               subtitle="Real-time monitoring & algo execution status" />
//   <PageHeader eyebrow="Live · NSE/BSE" live title="Market" accent="Watch" subtitle="…" />

import React from "react";
import { cx } from "./cx";
import { useBreakpoint } from "../../hooks/useBreakpoint";

export const PageHeader = ({
  eyebrow,
  live = false,
  title,
  accent,
  subtitle,
  actions,
  className,
  ...props
}) => {
  const { isPhone } = useBreakpoint();

  // On phone the AppShell's MobileHeader owns page identity (title + subtitle),
  // so the in-page identity block is redundant. Drop it, but keep any `actions`
  // (page-level controls) in a compact right-aligned row so nothing is lost.
  //
  // NO horizontal padding here: every page that passes `actions` already sets its
  // own `px-4 md:px-6`, so the old `0 16px` double-padded them — the action button
  // sat 32px from the edge while the content below it sat at 16px, visibly
  // out of line with the very cards it belongs to. The page owns the gutter.
  // The 16px bottom is the gap to that content, which was 0 (buttons hugged the
  // first card).
  if (isPhone) {
    if (!actions) return null;
    return (
      <div
        className={cx("ds-page-header-actions", className)}
        style={{ display: "flex", justifyContent: "flex-end", padding: "8px 0 16px" }}
        {...props}
      >
        {actions}
      </div>
    );
  }

  return (
    <header className={cx("ds-page-header", className)} {...props}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          {eyebrow && (
            <div className="ds-page-eyebrow">
              {live && <span className="ds-page-eyebrow-pulse" aria-hidden="true" />}
              {eyebrow}
            </div>
          )}
          <h1 className="ds-page-title">
            {title}
            {accent && (
              <>
                {" "}
                <span className="ds-page-accent">{accent}</span>
              </>
            )}
          </h1>
          {subtitle && <p className="ds-page-subtitle">{subtitle}</p>}
        </div>
        {actions && <div style={{ flexShrink: 0 }}>{actions}</div>}
      </div>
    </header>
  );
};

export default PageHeader;
